'use client';

import React, { useRef, useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Section } from '@/components/ui/Section';
import { Button } from '@/components/ui/Button';
import styles from './KioskShowcase.module.css';

export interface KioskShowcaseProps {
  title: string;
  subtitle: string;
  altTexts?: {
    blue?: string;
    orange?: string;
    pink?: string;
    black?: string;
  };
  ctaText?: string;
  ctaUrl?: string;
  ctaId?: string;
  ctaPosition?: string;
}

/**
 * KioskShowcase Component
 * Displays all 4 kiosk variants with floating animation effect
 * On mobile: auto-playing slider with touch support
 */
export function KioskShowcase({ 
  title, 
  subtitle,
  altTexts,
  ctaText,
  ctaUrl,
  ctaId,
  ctaPosition
}: KioskShowcaseProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isDesktop, setIsDesktop] = useState(true);
  const sliderRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);

  // Detect mobile and desktop for smart image loading
  useEffect(() => {
    const checkViewport = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsDesktop(width > 768);
    };
    checkViewport();
    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, []);

  // Auto-play slider on mobile
  useEffect(() => {
    if (!isMobile) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 4);
    }, 4000); // Change every 4 seconds

    return () => clearInterval(interval);
  }, [isMobile]);

  // ✅ PERFORMANCE FIX: SSR-friendly visibility with animations
  // Content is always visible, animations trigger on mount for smooth experience
  useEffect(() => {
    // Make content visible immediately on mount (SSR-friendly)
    const kiosks = sectionRef.current?.querySelectorAll(`.${styles.kiosk}`);
    kiosks?.forEach((kiosk) => {
      kiosk.classList.add(styles.visible);
    });
  }, []);

  // Touch handlers for swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50; // Minimum swipe distance

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        // Swiped left - next slide
        setCurrentSlide((prev) => (prev + 1) % 4);
      } else {
        // Swiped right - previous slide
        setCurrentSlide((prev) => (prev - 1 + 4) % 4);
      }
    }

    touchStartX.current = 0;
    touchEndX.current = 0;
  };

  const kiosks = [
    {
      id: 'blue',
      src: '/images/product-variants/kiosk-blue.webp',
      alt: altTexts?.blue || 'Jamezz kiosk in blue - Self-ordering kiosk for hospitality with touchscreen POS integration',
      color: '#4A90E2',
    },
    {
      id: 'orange',
      src: '/images/product-variants/kiosk-orange.webp',
      alt: altTexts?.orange || 'Jamezz kiosk in orange - Self-service terminal for restaurants with contactless ordering',
      color: '#F5A623',
    },
    {
      id: 'pink',
      src: '/images/product-variants/kiosk-pink.webp',
      alt: altTexts?.pink || 'Jamezz kiosk in pink - Modern ordering kiosk for hospitality with QR scan and menu display',
      color: '#E91E63',
    },
    {
      id: 'black',
      src: '/images/product-variants/kiosk-zwart.webp',
      alt: altTexts?.black || 'Jamezz kiosk in black - Premium self-ordering system for restaurants and hotels',
      color: '#2C3E50',
    },
  ];

  return (
    <Section background="primary" paddingY="none">
      <div className={styles.showcase} ref={sectionRef}>
      <div className={styles.background}>
      {/* BACKUP - Gradient orbs (Removed 2026-02-11 for CLS fix)
           Uncomment to restore if needed
      <div className={styles.gradientOrb1} />
      <div className={styles.gradientOrb2} />
      */}
        <div className={styles.grid} />
      </div>

      <div className={styles.content}>
        <div className={styles.header}>
          <h1 className={styles.title} data-speakable="true">{title}</h1>
          <p className={styles.subtitle} data-speakable="true">{subtitle}</p>
        </div>

        <div 
          className={styles.kiosksGrid}
          ref={sliderRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={isMobile ? {
            transform: `translateX(-${currentSlide * 100}%)`,
            transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          } : {}}
        >
          {kiosks.map((kiosk, index) => (
            <div
              key={kiosk.id}
              className={styles.kiosk}
              style={{
                animationDelay: `${index * 0.15}s`,
              }}
            >
              <div className={styles.kioskInner}>
                <div
                  className={styles.kioskGlow}
                  style={{
                    background: `radial-gradient(circle at center, ${kiosk.color}40 0%, transparent 70%)`,
                  }}
                />
                <div className={styles.imageWrapper}>
                  <Image
                    src={kiosk.src}
                    alt={kiosk.alt}
                    width={400}
                    height={800}
                    className={styles.kioskImage}
                    loading={
                      isDesktop 
                        ? (index < 2 ? "eager" : "lazy")  // Desktop: eerste 2 eager
                        : (index === 0 ? "eager" : "lazy")  // Mobile: alleen eerste eager
                    }
                    priority={index === 0}
                    quality={85}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation dots - CSS hides on desktop, visible immediately on mobile */}
          <div className={styles.sliderDots}>
            {kiosks.map((_, index) => (
              <button
                key={index}
                className={`${styles.dot} ${index === currentSlide ? styles.dotActive : ''}`}
                onClick={() => setCurrentSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        
        {/* CTA Button */}
        {ctaText && ctaUrl && (
          <div className={styles.ctaWrapper}>
            <Link href={ctaUrl}>
              <Button
                variant="primary"
                size="lg"
                data-cta-id={ctaId}
                data-cta-position={ctaPosition}
                data-cta-variant="primary"
              >
                {ctaText}
              </Button>
            </Link>
          </div>
        )}
      </div>
      </div>
    </Section>
  );
}

